import type { CSSInterpolation } from '@emotion/serialize';
import { useMemo, useState } from 'react';

import IconColumns from '~icons/bx/columns';

import { Menu, MenuButton } from '@adflow/frontend/ui-common';
import type { ViewColumnTemplateDto } from '@adflow/shared/util-column-templates';

import type { DataTableFullBaseProps } from '../DataTableFull/DataTableFull';

import { ColumnTemplateEditor } from './ColumnTemplateEditor';
import { ColumnTemplateMenuList } from './ColumnTemplateMenuList';
import {
	useColumnTemplateDelete,
	useColumnTemplatesQuery,
	useDefaultColumnTemplateQuery,
} from './queries';
import { styles } from './styles';
import type { ColumnTemplateId, DataTableColumnProps } from './types';

interface ColumnTemplateButtonProps<T extends object>
	extends
		DataTableColumnProps<T>,
		Pick<DataTableFullBaseProps<T>, 'archived' | 'itemType' | 'minSticky'> {
	columnTemplateStyles?: CSSInterpolation;
	columnTemplateIconStyles?: CSSInterpolation;
	topOffset: number;
	float?: boolean;
	hideColumnTemplateButton?: boolean;
}

export function ColumnTemplateButton<T extends object>({
	columns,
	columnTemplateMeta: {
		activeColumnTemplate,
		selectColumnTemplate,
		changeColumnTemplate,
	},
	columnTemplateStyles,
	columnTemplateIconStyles,
	archived,
	itemType,
	minSticky,
	topOffset,
	float = true,
	hideColumnTemplateButton = false,
}: ColumnTemplateButtonProps<T>) {
	/**
	 * The ID of the column template that is currently being edited or 'new' if a
	 * new column template is being created. If no column template is being
	 * edited, this is `undefined`.
	 */
	const [editingColumnTemplate, setEditingColumnTemplate] = useState<
		ColumnTemplateId | 'new'
	>();

	const openColumnTemplateEditor = (templateId: ColumnTemplateId | 'new') => {
		if (templateId !== 'new') {
			selectColumnTemplate(templateId);
		}
		setEditingColumnTemplate(templateId);
	};

	const closeColumnTemplateEditor = () => {
		setEditingColumnTemplate(undefined);
	};

	const { data: allTemplateData } = useColumnTemplatesQuery({
		type: itemType,
	});

	const { data: columnDefault } = useDefaultColumnTemplateQuery({
		type: itemType,
	});
	const { mutateAsync: deleteColumnTemplate } = useColumnTemplateDelete();

	const [
		savedAllColumnTemplates,
		savedColumnTemplates,
		defaultColumnTemplates,
	] = useMemo(() => {
		if (!allTemplateData) {
			return [[], [], []] as ViewColumnTemplateDto[][];
		}

		const columnTemplates = allTemplateData.items;

		const savedAllColumnTemplates_: ViewColumnTemplateDto[] = [];
		const savedColumnTemplates_: ViewColumnTemplateDto[] = [];
		const defaultColumnTemplates_: ViewColumnTemplateDto[] = [];

		for (const columnTemplate of columnTemplates) {
			// Show only one default column template
			if (columnTemplate.id === columnDefault?.id) {
				defaultColumnTemplates_.push(columnTemplate);
			} else if (columnTemplate.savedAll) {
				savedAllColumnTemplates_.push(columnTemplate);
			} else {
				savedColumnTemplates_.push(columnTemplate);
			}
		}

		return [
			savedAllColumnTemplates_,
			savedColumnTemplates_,
			defaultColumnTemplates_,
		];
	}, [allTemplateData, columnDefault]);

	return (
		<>
			<Menu
				trigger={
					<MenuButton
						css={[
							styles.columnButton,
							float && styles.floatingColumnButton(topOffset),
							archived && styles.headerCellArchived,
							hideColumnTemplateButton && { display: 'none' },
							columnTemplateStyles,
						]}
					>
						<IconColumns css={columnTemplateIconStyles} data-inline="false" />
					</MenuButton>
				}
			>
				{({ setOpen }) => (
					<ColumnTemplateMenuList
						templates={[
							savedAllColumnTemplates!,
							savedColumnTemplates!,
							defaultColumnTemplates!,
						]}
						createNewColumnTemplate={() => {
							openColumnTemplateEditor('new');
						}}
						activeColumnTemplateId={activeColumnTemplate?.id}
						setOpen={setOpen}
						selectColumnTemplate={selectColumnTemplate}
						openColumnTemplateEditor={openColumnTemplateEditor}
					/>
				)}
			</Menu>

			{editingColumnTemplate ? (
				<ColumnTemplateEditor
					isLoading={
						// eslint-disable-next-line react/jsx-no-leaked-render
						editingColumnTemplate !== 'new' &&
						// TODO: Handle loading state more explicitly to avoid bugs.
						editingColumnTemplate !== activeColumnTemplate?.id
					}
					template={
						editingColumnTemplate === 'new' ? undefined : activeColumnTemplate
					}
					columns={columns}
					closeColumnTemplateEditor={closeColumnTemplateEditor}
					deleteColumnTemplate={async (id) => {
						closeColumnTemplateEditor();
						await deleteColumnTemplate({ id });
						selectColumnTemplate(undefined);
					}}
					changeColumnTemplate={async (values) => {
						await changeColumnTemplate(values);
						closeColumnTemplateEditor();
					}}
					savedColumnTemplatesCount={savedColumnTemplates!.length}
					savedAllColumnTemplatesCount={savedAllColumnTemplates!.length}
					minSticky={minSticky}
					itemType={itemType}
				/>
			) : null}
		</>
	);
}
